// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.LoginActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
// $FlowFixMe
import curry from 'lodash/fp/curry';
import AccountConstants from 'logic/data/Izenda.AccountConstants';
import AccountStore from 'logic/Izenda.AccountStore';
import AppStore from 'logic/Izenda.AppStore';
import AuthorizationStore from 'logic/Izenda.AuthorizationStore';
import ProvisionMapDataStore from 'logic/Izenda.ProvisionMapDataStore';
import uiState from 'logic/Izenda.UiState';
import AccountUtils from 'modules/Account/Izenda.AccountUtils';
import compose from 'recompose/compose';
import withHandlers from 'recompose/withHandlers';
import withProps from 'recompose/withProps';
import localizer from 'share/localization/Izenda.Localizer';
import routeUtils from 'share/utils/Izenda.RouteUtils';

const KEYBOARD_ENTER_CODE = 13;

/**
 * Check does need to login.
 */
function isLoggedIn() {
  return component => {
    const {
      props: { authorization, router }
    } = component;
    if (!uiState.needLogin && authorization.checkLoginStatus()) {
      const defaultLink = authorization.getDefaultLink(routeUtils);
      router.push(defaultLink);
      return true;
    }

    return false;
  };
}

/**
 * Remove auth's cookies.
 */
function init() {
  return component => {
    const { store, authorization } = component.props;
    uiState.resetExportingLoadings();
    authorization.clear();
    AccountUtils.checkShowOrganization()(store);
  };
}

/**
 * Register event handler
 */
function registerEventHandler(): any {
  return function register(component) {
    // $FlowFixMe
    $izenda(document).on('keypress', e => {
      const key = e.which;
      if (key === KEYBOARD_ENTER_CODE) {
        return signIn(component.props);
      }
    });
  };
}

/**
 * Unregister event handler
 */
function unregisterEventHandler() {
  return function unregister() {
    // $FlowFixMe
    $izenda(document).off('keypress');
  };
}

/**
 * Handle on change tenant name.
 * @param  {Object} props
 * @param  {String} value
 */
function onChangeTenantName(props: any, event) {
  props.store.model.updateProperty('tenantName', event.target.value);
}

/**
 * Handle on change user name.
 * @param  {Object} props
 * @param  {String} value
 */
function onChangeUserName(props: any, event: any) {
  props.store.model.updateProperty('userName', event.target.value);
}

/**
 * Handle on change password.
 * @param  {Object} props
 * @param  {String} value
 */
function onChangePassword(props: any, event: any) {
  props.store.model.updateProperty('password', event.target.value);
}

/**
 * Sign in action.
 * @param  {Object} props
 */
function signIn(props: any) {
  const store = props.store;
  const url = buildRedirectTo(props);
  const loader = props.loader;

  store.model.updateProperty('message');
  loader.startLoading();

  props.validation
    .validate()
    .then(result => {
      if (result) {
        return AppStore.licenseInfoStore.getLicenseStatus().then(licenseStatus => {
          if (licenseStatus) {
            const expiredLicense = licenseStatus.numberOfDayToExpire < 0;
            uiState.updateLicenseStatus(expiredLicense);
          }

          return result;
        });
      }
      return result;
    })
    .then(result => {
      if (result) {
        return store.login().then(result => {
          const data = result.data || {};

          if (result.success && !data.systemAdmin && uiState.expiredLicense) {
            return props.confirmMessage('error', localizer.getText('COMMON_MESSAGES_EXPIRED_LICENSE'));
          }

          if (result.success && !data.isExpired) {
            uiState.setNeedLogin(false);
            store.model.resetForLogin();

            return AccountUtils.setUpAuthInfo(result, props, url, true).then(() =>
              ProvisionMapDataStore.getProvisioningStatus()
            );
          }

          if (data.isExpired) {
            return props.confirmMessage('info', AccountConstants.MESSAGES.M04).then(() => {
              const resetPasswordLink = routeUtils.getRouteUrl(routeUtils.getConfigRoute('Account', 'ResetPassword'), {
                id: data.id
              });
              store.model.resetForLogin();
              props.router.push(resetPasswordLink);
              return Promise.resolve(true);
            });
          }

          return store.loadGeneralErrorMessage(result.messages);
        });
      }
      return Promise.resolve(false);
    })
    .finally(loader.endLoading.bind(loader));
}

/**
 * Handle on click sign in.
 * @param  {Object} props
 */
function onClickSignIn(props: any) {
  return () => signIn(props);
}

/**
 * Build redirect to URL.
 * @param  {Object} props
 */
function buildRedirectTo(props: any): string {
  const query = props.location.query;
  return query.redirectTo ? query.redirectTo : null;
}

/**
 * Handle on click forgot password.
 * @param  {Object} props
 * @param  {Object} obj
 */
function onClickForgotPassword(props: any): any {
  return () => {
    props.store.loadSystemConfig().then(result => {
      if (!result.numberOfQuestionProfile) {
        return props.confirmMessage('error', AccountConstants.MESSAGES.M08).then(() => Promise.resolve(false));
      }

      const uriConfig = {
        pathname: routeUtils.getRouteUrl(routeUtils.getConfigRoute('Account', 'ForgotPassword')),
        state: {}
      };
      props.store.model.userName && (uriConfig.state.userName = props.store.model.userName);
      props.store.model.tenantName && (uriConfig.state.tenantName = props.store.model.tenantName);
      props.router.push(uriConfig);

      return Promise.resolve(true);
    });
  };
}

export default compose(
  withProps({
    store: new AccountStore(),
    authorizationStore: new AuthorizationStore()
  }),
  withHandlers({
    isLoggedIn,
    init,
    registerEventHandler,
    unregisterEventHandler,
    onChangeTenantName: curry(onChangeTenantName),
    onChangeUserName: curry(onChangeUserName),
    onChangePassword: curry(onChangePassword),
    onClickSignIn: onClickSignIn,
    onClickForgotPassword: onClickForgotPassword
  })
);



// WEBPACK FOOTER //
// ./ui/modules/Account/Izenda.LoginActions.js